import React from 'react'

import { Stack } from '@mui/material'

import { Skeleton } from '@repo/ui'
import { Header, InfoBlock, InfoRow, InfoItem, Table } from 'components'

/**
 * Loading placeholder for the owner detail page (§19 — a skeleton fills the SAME space the loaded
 * content will occupy, so nothing shifts in).
 *
 * ## What it replaces
 *
 * The old loading branch rendered `<Header title="" breadcrumbs />` — `title=""` is falsy, so the
 * Header rendered NO title node at all and no avatar, no status chip and no action buttons — then an
 * `InfoBlock` of five hand-rolled bars, then a single `Skeleton height={400}` standing in for the
 * certificates table. Measured against the loaded page, that was wrong in four places at once:
 *
 *   1. no `breadcrumbsOveride` → `Header` skips the breadcrumb trail, which also flips its
 *      `showsBreadcrumbTrail` branch on and applies a `top: -24.5px` pull. The loaded page has a
 *      real trail (33px row, no pull), so EVERYTHING below sat ~25px too high.
 *   2. no avatar → the title row collapsed from 64px (the avatar's height) to 49px.
 *   3. no action buttons → four controls (472px of them) appeared out of nothing.
 *   4. the table was a 400px box against a `fillBottom` grid that measures ~956px here, so the
 *      bottom two thirds of the page grew in.
 *
 * ## Measured, not guessed
 *
 * Off the rendered page (`/owners/:id`, an owner with 10 certificates, viewport 1711px, content
 * column x=104 w=1583):
 *
 *   wayfinding row   y=24  h=33   (the breadcrumb nav itself 213x25 at y=32)
 *   title row        y=69  h=64   — 64 because the avatar is 64; the title's own h5 is 31 tall
 *   avatar           64x64 at x=104
 *   title            w=209 h=31 at x=180   ("john@example.com")
 *   status chip      76x32 at x=401 (pill)
 *   header actions   Archive 116 / Edit 90 / Delete 109 / Transfer 120, all h=49, group w=472
 *   header total     y=24  h=133
 *   InfoBlock        y=157 h=86  — one row of five 316px items, each 84 tall
 *   table box        y=259        — ActionLine 65 tall, then the grid at y=324
 *
 * ## Construction, not constants
 *
 * Only the header's own widths are literals here (a button's width and a title's width are not
 * derivable, and neither affects vertical footprint). Everything that decides HEIGHT is the real
 * component:
 *
 *   - the avatar is `avatar={null}`, which `Header` already renders as a 64px circular skeleton, so
 *     the 64px title row is correct by construction;
 *   - the status chip is `status={null}`, `Header`'s own pill placeholder;
 *   - the InfoBlock renders the REAL `InfoBlock`/`InfoRow`/`InfoItem` with `value={undefined}`,
 *     which `InfoItem` already draws as a `<Skeleton width={150} />` — so its 86px is correct by
 *     construction and stays correct if a sixth field is added to the real block;
 *   - the table is the REAL `Table` in its `loading` state, which draws a column-aware skeleton
 *     overlay and sizes itself with the same `fillBottom` measurement. That is why this component
 *     takes `columns`: reproducing the grid's height with a number would have been a fifth stale
 *     constant, and it would go stale the moment a column's width changes.
 */
export const OwnerPageSkeleton = ({ columns }: { columns: any }) => {
  /**
   * A header action button: the real widths, at the height `headerActionGroupSx` in Header.tsx
   * enforces on a header button (40 — `actionSize` default `medium`; measured 40 on the live page).
   *
   * A literal only because Header does not export it; a `HEADER_ACTION_HEIGHT` constant is the real
   * fix, and until it exists this has to track that file. It does NOT affect footprint — the title
   * row carries its own `minHeight: 49`, so the header measures 133 here either way — so getting it
   * wrong is a visual mismatch, not a shift.
   */
  const ActionSkeleton = ({ width }: { width: number }) => (
    <Skeleton variant="rounded" width={width} height={40} sx={{ borderRadius: 2 }} />
  )

  return (
    <>
      <Header
        // Not the word "Loading…": text of one unknown length being swapped for text of another is
        // the shift this file exists to prevent. 209px is the measured width of a representative
        // owner title (an email address).
        title={<Skeleton width={209} height={31} />}
        // `null`, not omitted — `Header` renders its own 64px circular placeholder for this, which
        // is what keeps the title row at the loaded page's 64px instead of collapsing to 49.
        avatar={null as any}
        // Ditto: `Header`'s own pill placeholder stands in for the status chip.
        status={null as any}
        breadcrumbs={true}
        // A skeleton crumb, NOT omitted. `Header` renders the trail only when this is truthy, and
        // omitting it ALSO trips the no-trail branch that pulls the whole header up 24.5px — so
        // leaving this out moved every region below it, not just this row.
        breadcrumbsOveride={<Skeleton width={150} height={16} />}
        rightComponents={
          <Stack direction="row" spacing={3} alignItems="center">
            {/* Archive's width, not Unarchive's: an owner that is NOT archived is the common case,
                and which one renders is unknowable before the fetch returns. */}
            <ActionSkeleton width={116} />
            <ActionSkeleton width={90} />
            <ActionSkeleton width={109} />
            <ActionSkeleton width={120} />
          </Stack>
        }
      />

      {/* The real block, same `mb: 4`, same five labels — see the note on construction above. */}
      <InfoBlock sx={{ mb: 4 }}>
        <InfoRow>
          <InfoItem label="Registration date" value={undefined} />
          <InfoItem label="Email" value={undefined} />
          <InfoItem label="Phone" value={undefined} />
          <InfoItem label="Age" value={undefined} />
          <InfoItem label="Gender" value={undefined} />
        </InfoRow>
      </InfoBlock>

      {/* The real table in its loading state: same columns, same `fillBottom`, so the toolbar row
          and the grid occupy exactly the space they will occupy loaded. */}
      <Table loading fillBottom columns={columns} rows={[]} showSelection={false} />
    </>
  )
}
